import React from 'react';
import {
  Award,
  BookOpen,
  ChevronRight,
  Gem,
  Heart,
  Megaphone,
  Scale,
  Smile,
  Sparkles,
  Sun,
  Wind,
  Zap,
} from 'lucide-react';
import { TaskyCheckbox, TaskyTextarea, TaskyInput } from '../tasky';
import { AspectRatio, CmType, GenreTag, MoodTag, TargetSeconds, Tempo, VocalGender } from '../../services/api/songAdService';

/**
 * 作成設定（CM構成タイプ・雰囲気タグ・ボーカル・テンポ・尺・比率・イントロ・自由指示・詳細設定）の
 * 入力フォーム本体。mockup.html の「作成設定」画面（RENDERERS.setup）を移植。
 *
 * - 新規作成パネル（SongAdCreatePanel）と、既存プロジェクトの「条件を変更」（LyricsStep内）の
 *   両方から共有する（商品選択・タイトル・送信ボタンは呼び出し側が持つ）
 * - キー・ラベルは spec §7 準拠。ただし列挙値は GOAL.md「API変更仕様の要点」が正
 *   （mockup.html 内部キーの refreshing/moving 等とは異なる。詳細は songAdService.ts のコメント参照）
 */

export interface SongAdSettingsValue {
  cmType: CmType;
  /** 音楽ジャンル（v5 c）。null＝おまかせ（曲調・雰囲気のみでLyriaプロンプトを合成） */
  genreTag: GenreTag | null;
  moodTag: MoodTag;
  vocalGender: VocalGender;
  tempo: Tempo;
  targetSeconds: TargetSeconds;
  /**
   * v6 F7: 廃止済みの尺（現状は15秒のみ。backendの `RETIRED_TARGET_SECONDS`）が
   * プロジェクトに残っている場合の実測値。非nullの間は動画の長さセレクタに専用の
   * 無効化タイル「◯◯秒（提供終了）」を表示し、`targetSeconds`（30/45/60）はどれも選択状態にしない。
   * 3択のいずれかをユーザーが選ぶと自動的にnullへ戻る（＝以後は通常の3択として保存される）。
   * ここがnullでない間は保存時に `target_seconds` をpayloadに含めない
   * （15を送るとbackendのvalidationで422になるため。送らなければbackendの部分更新で現在値が保持される）
   */
  retiredTargetSeconds: number | null;
  aspectRatio: AspectRatio;
  intro: boolean;
  freeText: string;
  requiredWordsText: string;
  bannedWordsText: string;
  cta: string;
}

export const defaultSongAdSettings: SongAdSettingsValue = {
  cmType: 'balance',
  genreTag: null,
  moodTag: 'bright',
  vocalGender: 'auto',
  tempo: 'normal',
  targetSeconds: 30,
  retiredTargetSeconds: null,
  aspectRatio: '9:16',
  intro: false,
  freeText: '',
  requiredWordsText: '',
  bannedWordsText: '',
  cta: '',
};

/** `sec` が現行の選択肢（30/45/60）に含まれるかどうか（v6 F7） */
export const isValidTargetSeconds = (sec: number): sec is TargetSeconds =>
  sec === 30 || sec === 45 || sec === 60;

/** 「必ず含める言葉」「避ける言葉」入力（、/,/改行区切り）を配列へ変換する */
export const splitWordsInput = (text: string): string[] =>
  text
    .split(/[,、\n]/)
    .map((w) => w.trim())
    .filter(Boolean);

export const joinWordsInput = (words: string[]): string => words.join('、');

interface CmTypeOption {
  key: CmType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  rec: TargetSeconds[];
  desc: string;
}

export const CM_TYPES: CmTypeOption[] = [
  {
    key: 'catchcopy',
    label: 'キャッチコピー重視',
    icon: Megaphone,
    // v6 F7: 15秒は廃止（Lyriaが尺を扱えず歌詞の大半が入らない）。30秒のみ推奨に残す
    rec: [30],
    desc: '商品名や短いキャッチコピーを繰り返し、耳に残る歌にします。認知獲得・短尺広告向け。',
  },
  {
    key: 'balance',
    label: 'バランス型',
    icon: Scale,
    rec: [30, 45],
    desc: 'キャッチコピーを中心に、特徴とベネフィットも自然に伝わる構成。迷ったらこれ。',
  },
  {
    key: 'explain',
    label: '説明重視',
    icon: BookOpen,
    rec: [45, 60],
    desc: '特徴・仕組み・メリットを具体的に伝えます。新サービス紹介やBtoB向け。',
  },
];

interface GenreOption {
  key: GenreTag;
  label: string;
}

/** 音楽ジャンル12種（v5 c）。「曲調・雰囲気」8種とは別軸で併存する */
export const GENRE_TAGS: GenreOption[] = [
  { key: 'pop', label: 'ポップ' },
  { key: 'rock', label: 'ロック' },
  { key: 'ballad', label: 'バラード' },
  { key: 'edm', label: 'EDM' },
  { key: 'acoustic', label: 'アコースティック' },
  { key: 'hiphop', label: 'ヒップホップ' },
  { key: 'citypop', label: 'シティポップ' },
  { key: 'anime', label: 'アニソン' },
  { key: 'jazz', label: 'ジャズ' },
  { key: 'lofi', label: 'Lo-Fi' },
  { key: 'metal', label: 'メタル' },
  { key: 'hardcore', label: 'ハードコア' },
];

interface MoodOption {
  key: MoodTag;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  hint: string;
}

export const MOOD_TAGS: MoodOption[] = [
  { key: 'bright', label: '明るい', icon: Sun, hint: '親しみやすく、幅広い商品に' },
  { key: 'fresh', label: '爽やか', icon: Wind, hint: '清潔感・透明感・軽快さ' },
  { key: 'energetic', label: '元気', icon: Zap, hint: 'テンション高く、勢いよく' },
  { key: 'funny', label: 'おもしろい', icon: Smile, hint: '意外性・言葉遊びで記憶に残す' },
  { key: 'cute', label: 'かわいい', icon: Heart, hint: '柔らかく、口ずさみやすく' },
  { key: 'stylish', label: 'おしゃれ', icon: Gem, hint: '都会的で洗練された印象' },
  { key: 'emotional', label: '感動的', icon: Sparkles, hint: '共感とストーリー性を重視' },
  { key: 'premium', label: '高級感', icon: Award, hint: '落ち着きと上質さ、余白' },
];

export const VOCAL_OPTIONS: { key: VocalGender; label: string }[] = [
  { key: 'auto', label: 'おまかせ' },
  { key: 'female', label: '女性ボーカル' },
  { key: 'male', label: '男性ボーカル' },
];

export const TEMPO_OPTIONS: { key: Tempo; label: string }[] = [
  { key: 'fast', label: '早め' },
  { key: 'normal', label: '普通' },
  { key: 'slow', label: 'ゆっくり' },
];

/**
 * v6 F7: 15秒を削除し30/45/60の3択にした。実測でLyriaが15秒指定を扱えず
 * （3行指定で実尺102秒超・行境界カットで3行中1行しか入らない）、歌詞が少ないほど
 * 引き延ばす性質があるため行数を減らしても改善しない、という結論から廃止した。
 */
const DURATIONS: { sec: TargetSeconds; hint: string }[] = [
  { sec: 30, hint: 'コピーと特徴をバランスよく' },
  { sec: 45, hint: '複数の特徴・メリットを盛り込む' },
  { sec: 60, hint: '仕組みや内容を詳しく伝える' },
];

const ASPECTS: { key: AspectRatio; shape: string; hint: string }[] = [
  { key: '9:16', shape: '縦長', hint: 'TikTok・Reels・Shorts' },
  { key: '16:9', shape: '横長', hint: 'YouTube・Webサイト' },
  { key: '1:1', shape: '正方形', hint: 'SNSフィード' },
];

const selCardClass = (selected: boolean): string =>
  `rounded-xl border text-left transition-colors ${
    selected
      ? 'border-primary bg-primary/5'
      : 'border-warm-border bg-white hover:border-primary/40'
  }`;

const segButtonClass = (selected: boolean): string =>
  `flex-1 px-3 py-2 text-sm transition-colors ${
    selected ? 'bg-primary text-white font-medium' : 'text-gray-600 hover:bg-gray-100'
  }`;

export interface SongAdSettingsFormProps {
  value: SongAdSettingsValue;
  onChange: (patch: Partial<SongAdSettingsValue>) => void;
  disabled?: boolean;
  advancedDefaultOpen?: boolean;
  productNamePlaceholder?: string;
}

const SongAdSettingsForm: React.FC<SongAdSettingsFormProps> = ({
  value,
  onChange,
  disabled = false,
  advancedDefaultOpen = false,
  productNamePlaceholder,
}) => {
  const [advOpen, setAdvOpen] = React.useState(advancedDefaultOpen);
  const recDurations = CM_TYPES.find((c) => c.key === value.cmType)?.rec ?? [];
  const selectedMood = MOOD_TAGS.find((m) => m.key === value.moodTag) ?? MOOD_TAGS[0];

  return (
    <div className="space-y-5">
      {/* CM構成タイプ */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">CM構成タイプ</p>
        <div className="grid md:grid-cols-3 gap-3">
          {CM_TYPES.map((c) => {
            const selected = value.cmType === c.key;
            return (
              <button
                key={c.key}
                type="button"
                disabled={disabled}
                onClick={() => onChange({ cmType: c.key })}
                className={`${selCardClass(selected)} p-3.5`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <c.icon className={`w-[19px] h-[19px] ${selected ? 'text-primary' : 'text-gray-400'}`} />
                  <span className="text-sm font-semibold text-dark">{c.label}</span>
                  <span className="ml-auto text-[10px] text-gray-400 bg-warm-surface rounded-full px-2 py-0.5">
                    推奨 {c.rec.join('・')}秒
                  </span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{c.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* 音楽ジャンル（v5 c）。曲調・雰囲気とは別軸で併存する */}
      <div>
        <div className="flex items-baseline gap-2 mb-2">
          <p className="text-sm font-medium text-gray-700">音楽ジャンル</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange({ genreTag: null })}
            className={`rounded-full pl-3 pr-3.5 py-1.5 text-[13px] border transition-colors ${
              value.genreTag === null
                ? 'bg-primary text-white border-primary'
                : 'border-warm-border text-gray-600 hover:border-primary'
            }`}
          >
            おまかせ
          </button>
          {GENRE_TAGS.map((g) => {
            const selected = value.genreTag === g.key;
            return (
              <button
                key={g.key}
                type="button"
                disabled={disabled}
                onClick={() => onChange({ genreTag: g.key })}
                className={`rounded-full pl-3 pr-3.5 py-1.5 text-[13px] border transition-colors ${
                  selected
                    ? 'bg-primary text-white border-primary'
                    : 'border-warm-border text-gray-600 hover:border-primary'
                }`}
              >
                {g.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 曲調・雰囲気タグ */}
      <div>
        <div className="flex items-baseline gap-2 mb-2">
          <p className="text-sm font-medium text-gray-700">曲調・雰囲気</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {MOOD_TAGS.map((m) => {
            const selected = value.moodTag === m.key;
            return (
              <button
                key={m.key}
                type="button"
                title={m.hint}
                disabled={disabled}
                onClick={() => onChange({ moodTag: m.key })}
                className={`flex items-center gap-1.5 rounded-full pl-3 pr-3.5 py-1.5 text-[13px] border transition-colors ${
                  selected
                    ? 'bg-primary text-white border-primary'
                    : 'border-warm-border text-gray-600 hover:border-primary'
                }`}
              >
                <m.icon className="w-4 h-4" />
                <span>{m.label}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-gray-400 mt-1.5">{selectedMood.hint}</p>
      </div>

      {/* ボーカル / テンポ */}
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">ボーカル</p>
          <div className="inline-flex w-full rounded-lg overflow-hidden border border-warm-border">
            {VOCAL_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                disabled={disabled}
                onClick={() => onChange({ vocalGender: opt.key })}
                className={segButtonClass(value.vocalGender === opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">テンポ</p>
          <div className="inline-flex w-full rounded-lg overflow-hidden border border-warm-border">
            {TEMPO_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                disabled={disabled}
                onClick={() => onChange({ tempo: opt.key })}
                className={segButtonClass(value.tempo === opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* イントロ */}
      <div>
        <TaskyCheckbox
          checked={value.intro}
          disabled={disabled}
          onChange={(e) => onChange({ intro: e.target.checked })}
          wrapperClassName="bg-white border border-warm-border rounded-lg px-3.5 py-2.5 w-full"
          label={
            <span className="inline-flex flex-wrap items-baseline gap-2">
              <span className="text-sm text-gray-700">イントロをつける</span>
              <span className="text-[11px] text-gray-400">
                前奏と導入フレーズから始めます。オフなら約0.3秒で歌い出し（広告向け・デフォルト）
              </span>
            </span>
          }
        />
      </div>

      {/* 動画の長さ */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">動画の長さ</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* v6 F7: 廃止済みの尺（15秒）が現在値の間だけ、選べない専用タイルとして表示する。
              選択肢から単に消すと現在値を見失う（=何も選ばれていないように見える）ため */}
          {value.retiredTargetSeconds !== null && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/5 text-left p-3">
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-dark">{value.retiredTargetSeconds}</span>
                <span className="text-xs text-gray-500">秒</span>
                <span className="ml-auto text-[10px] text-destructive bg-white rounded-full px-2 py-0.5">
                  提供終了
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">下から新しい長さを選んでください</p>
            </div>
          )}
          {DURATIONS.map((d) => {
            const selected = value.retiredTargetSeconds === null && value.targetSeconds === d.sec;
            const recommended = recDurations.includes(d.sec);
            return (
              <button
                key={d.sec}
                type="button"
                disabled={disabled}
                onClick={() => onChange({ targetSeconds: d.sec, retiredTargetSeconds: null })}
                className={`${selCardClass(selected)} p-3`}
              >
                <div className="flex items-baseline gap-1">
                  <span className="text-lg font-bold text-dark">{d.sec}</span>
                  <span className="text-xs text-gray-500">秒</span>
                  {recommended && (
                    <span className="ml-auto text-[10px] text-primary bg-primary/10 rounded-full px-2 py-0.5">
                      推奨
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{d.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* アスペクト比 */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">アスペクト比</p>
        <div className="flex gap-3 flex-wrap">
          {ASPECTS.map((a) => {
            const selected = value.aspectRatio === a.key;
            return (
              <button
                key={a.key}
                type="button"
                disabled={disabled}
                onClick={() => onChange({ aspectRatio: a.key })}
                className={`${selCardClass(selected)} px-4 py-2.5 flex items-center gap-3`}
              >
                <span
                  className={`block rounded-[3px] border-2 ${
                    selected ? 'border-primary bg-primary/10' : 'border-gray-300'
                  }`}
                  style={{
                    width: a.key === '16:9' ? 30 : a.key === '1:1' ? 22 : 16.9,
                    height: a.key === '16:9' ? 16.9 : a.key === '1:1' ? 22 : 30,
                  }}
                />
                <span className="text-left">
                  <span className="block text-sm font-semibold text-dark">
                    {a.key}
                    <span className="text-xs font-normal text-gray-500">（{a.shape}）</span>
                  </span>
                  <span className="block text-[11px] text-gray-400">{a.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 自由指示 */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">曲調の自由記述（任意）</p>
        <TaskyTextarea
          value={value.freeText}
          disabled={disabled}
          onChange={(e) => onChange({ freeText: e.target.value.slice(0, 500) })}
          rows={2}
          maxLength={500}
          placeholder="例: 明るく前向きなCMソング。サビでキャッチーなフレーズを繰り返す。"
        />
      </div>

      {/* 詳細設定 */}
      <div>
        <button
          type="button"
          onClick={() => setAdvOpen((v) => !v)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ChevronRight className={`w-[18px] h-[18px] transition-transform ${advOpen ? 'rotate-90' : ''}`} />
          <span>詳細設定</span>
        </button>
        {advOpen && (
          <div className="mt-3 bg-white border border-warm-border rounded-xl p-4 grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">必ず含める言葉</label>
              <TaskyInput
                value={value.requiredWordsText}
                disabled={disabled}
                onChange={(e) => onChange({ requiredWordsText: e.target.value })}
                placeholder={`例: ${productNamePlaceholder ?? '商品名'}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">避ける言葉</label>
              <TaskyInput
                value={value.bannedWordsText}
                disabled={disabled}
                onChange={(e) => onChange({ bannedWordsText: e.target.value })}
                placeholder="例: 安い、No.1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">CTA文言</label>
              <TaskyInput
                value={value.cta}
                disabled={disabled}
                onChange={(e) => onChange({ cta: e.target.value })}
                placeholder="例: 今すぐチェック"
              />
            </div>
            <p className="md:col-span-3 text-[11px] text-gray-400">
              楽器編成やBPMは「曲調の自由記述」に書いてください。
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SongAdSettingsForm;
